import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  startNavigation,
  completeNavigation,
  getNavigationStats
} from '../utils/navigationPerformanceTracker';

interface NavigationPerformanceHook {
  startNavigation: (fromPage?: string) => void;
  completeNavigation: (toPage?: string) => void;
  getStats: () => {
    threshold: number;
    hasPendingNavigation: boolean;
    pendingNavigationStartTime: number | null;
  };
  currentPath: string;
}

export const useNavigationPerformance = (): NavigationPerformanceHook => {
  const location = useLocation();
  const previousLocationRef = useRef<string>('');
  const isNavigatingRef = useRef<boolean>(false);

  // Memoized navigation functions
  const handleStartNavigation = useCallback((fromPage?: string) => {
    startNavigation(fromPage);
    isNavigatingRef.current = true;
  }, []);

  const handleCompleteNavigation = useCallback((toPage?: string) => {
    // Defer completion to next animation frame so we measure time after paint
    requestAnimationFrame(() => {
      completeNavigation(toPage);
      isNavigatingRef.current = false;
    });
  }, []);

  // Track route changes automatically
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const previousPath = previousLocationRef.current;

    if (previousPath && previousPath !== currentPath) {
      if (!isNavigatingRef.current) {
        // Start navigation if not already started
        handleStartNavigation(previousPath);
      }

      // Complete the navigation
      handleCompleteNavigation(currentPath);
    }

    // Update the previous location reference
    previousLocationRef.current = currentPath;
  }, [location, handleStartNavigation, handleCompleteNavigation]);

  // Initialize on mount
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    previousLocationRef.current = currentPath;
  }, [location.pathname, location.search]);

  return {
    startNavigation: handleStartNavigation,
    completeNavigation: handleCompleteNavigation,
    getStats: getNavigationStats,
    currentPath: location.pathname + location.search
  };
};

export default useNavigationPerformance;
